/**
 * Injection seam for `@heidi/plugin-core`.
 *
 * WHY THIS EXISTS AT ALL, because the obvious alternative is banned rather than
 * merely worse. Three of the modules this package holds log — `windows/window.ts`
 * ×2, `events/managed-listen.ts` ×1, `events/recording-lifecycle.ts` ×3 — and
 * the logger they logged through app-side is `@heidi/runtime/logger`. This
 * package is `type:bridge`, and `packages/eslint-plugin-rules/module-boundaries.js`
 * lets `type:bridge` depend only on `type:bridge`, `type:util` and `type:schema`.
 * `@heidi/runtime` is `type:runtime`, so BOTH `@heidi/runtime/logger` and
 * `@heidi/runtime/platform` are out of reach — the boundary lint refuses them by
 * name. Adding `pino`, or deleting the six log lines, were the other two ways
 * out and both were refused: the first grows a second logging identity inside a
 * bridge, the second deletes desktop diagnostics that on-call reads.
 *
 * So the logger is INJECTED, the house `configure<Package>` shape `capture` and
 * `dictation` already use.
 *
 * IT DEFAULTS TO A NO-OP SINK RATHER THAN FAILING LOUD, and that is the one
 * place this seam deliberately departs from `configureDictationData`. IPC is the
 * desktop app's only channel to Rust: a `getPluginCoreConfig()` that throws
 * before the app bootstrap has run would turn a wiring omission into a dead
 * recorder, and every one of these six call sites is a diagnostic rather than a
 * behaviour. A missing bootstrap must cost logs, never IPC.
 *
 * Which makes the failure mode SILENCE, so the wiring is proven by a test
 * instead of by the absence of a crash: `src/composition/wiring/plugin-core-wiring.test.ts`
 * asserts the app bootstrap calls `configurePluginCore` and that the logger it
 * hands over is the real one.
 */

// The SDK, under the `import` condition — the same file `@heidi/runtime/platform`,
// `events/managed-listen.ts` and `windows/window.ts` resolve. See
// `DEFAULT_IS_TAURI` for why `require` here was a dual-module-instance bug.
import { isTauri as isTauriEnv } from '@tauri-apps/api/core';

/**
 * The four levels this package's modules actually call — nothing declared
 * speculatively.
 *
 * Structurally satisfied by `CustomLogger` from `@heidi/runtime/logger`, which is
 * what the app injects, without this package naming that type (it may not import
 * it). A structural subset also means a test double is four functions rather
 * than the nine-member logger interface.
 */
export interface PluginCoreLogger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

export interface PluginCoreConfig {
  logger: PluginCoreLogger;
  /**
   * Tauri-WebView detection. `events/recording-lifecycle.ts` is the one module in
   * this package that guards on it, and its app-side spelling was `isTauri` from
   * `@/tauri/utils` — a runtime re-export of `@heidi/runtime/platform`, which is
   * `type:runtime` and therefore out of reach. §1b names it alongside the logger
   * for exactly this reason: "the package takes its logger (and any `isTauri`
   * need) as an injected seam".
   *
   * INJECTED RATHER THAN RECOMPUTED, and the first attempt at this change proves
   * why. Evaluating the predicate from `@tauri-apps/api/core` in the module body
   * type-checked, passed the package suite, and broke
   * `src/hooks/desktop/useDesktopIdleStream.test.ts` in eighteen places: that
   * suite mocks `@tauri-apps/api/core` with an `invoke` and no `isTauri`, and
   * controls the guard through its `jest.mock('@/tauri/utils')`. A seam keeps the
   * guard controllable from the outside, which is what the mock contract was
   * always relying on.
   *
   * The app injects `@heidi/runtime/platform`'s `isTauri`, so there is ONE
   * detection authority at runtime and this is not a second one.
   */
  isTauri: () => boolean;
}

const noop = (): void => {};

/**
 * The safe default. Exported so the wiring test can assert the app replaced it
 * rather than merely that `configurePluginCore` is importable.
 */
export const NO_OP_LOGGER: PluginCoreLogger = {
  debug: noop,
  info: noop,
  warn: noop,
  error: noop,
};

/**
 * The default detection, and the reason the seam does not fail loud.
 *
 * Deliberately the same two conditions `packages/runtime/src/platform/is-tauri.ts`
 * applies — `typeof window !== 'undefined' && isTauriEnv()` over
 * `@tauri-apps/api/core`, through the same STATIC named import — so an
 * unconfigured read answers correctly instead of answering `false` and turning a
 * wiring omission into a silently dead recorder. There is no app input to lose:
 * the `NEXT_PUBLIC_IS_TAURI` dev-misconfiguration guard that once sat beside that
 * predicate moved into `configurePlatform()` at Wave 1 · P1, so the runtime
 * door's version is config-free too.
 *
 * THE IMPORT IS STATIC, AND `require` WAS A REAL BUG HERE. The first version of
 * this reached the SDK through `require('@tauri-apps/api/core')` on the reasoning
 * that the seam member is sync and so cannot `await`. That is true and still not a
 * licence to use `require`: `@tauri-apps/api` publishes conditional exports, and
 * `require` resolves `./core` to `core.cjs` while every other importer in the repo
 * — this package's own `events/managed-listen.ts`, `windows/window.ts`,
 * `@heidi/runtime/platform` — resolves it to `core.js` under the `import`
 * condition. Verified, not surmised: `require.resolve` and `import.meta.resolve`
 * return different files. Two module instances of the SDK is a class of defect
 * that shows up as an event listener registered on the instance nobody is
 * emitting from, and nothing local would have caught it.
 *
 * So the fallback loads the SDK exactly as the two sibling leaves already do. It
 * costs the door an eager `@tauri-apps/api/core` in a CJS context, which those two
 * leaves already cost it; in an ESM bundle `sideEffects: false` drops it, and the
 * module is in the web graph anyway through `@heidi/runtime/platform`.
 * `manifest-boundary.test.ts` pins the set of modules that reach the SDK statically
 * so a fourth one is a decision rather than a drift.
 *
 * `src/composition/wiring/plugin-core-wiring.test.ts` holds this fallback in agreement with the
 * runtime door, because it is the only place in the repo that can see both.
 */
const DEFAULT_IS_TAURI = (): boolean => typeof window !== 'undefined' && isTauriEnv();

const DEFAULTS: PluginCoreConfig = {
  logger: NO_OP_LOGGER,
  isTauri: DEFAULT_IS_TAURI,
};

let current: PluginCoreConfig = DEFAULTS;

/**
 * One-time app-bootstrap call (`src/composition/wiring/plugin-core-wiring.ts`, imported at
 * module scope from `_app.tsx`). IPC before this runs works exactly as it does
 * after; only the six diagnostic log lines are silent.
 */
export function configurePluginCore(config: PluginCoreConfig): void {
  current = config;
}

/**
 * Read the injected logger. A GETTER rather than a captured module constant so
 * wiring order can never race a module-scope consumer: a module evaluated before
 * the bootstrap still logs through whatever is current at CALL time.
 */
export function pluginCoreLogger(): PluginCoreLogger {
  return current.logger;
}

/** Read the injected Tauri predicate. A getter for the same reason. */
export function pluginCoreIsTauri(): boolean {
  return current.isTauri();
}

/** Test-only reset so a suite cannot leak a double into the next one. */
export function __resetPluginCoreForTests(): void {
  current = DEFAULTS;
}
